import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  extractAddonName,
  extractPlanName,
  isAddon,
  isPlan,
} from '../../auth/sign-up/utils/extract.stripe';
import {
  Bancos,
  BillingInfo,
  CardCreate,
  CardDefault,
  CardDetail,
  CardsList,
  InformacionPagoTranferencia,
  Metodos,
  PaymentList,
  PaymentListInvoice,
  Products,
  Sociedades,
  Subscriptions,
  SubscriptionsItems,
  SubStatus,
  TransferInfo,
} from './models/facturacion.interface';
import { PermisosUsuario } from '@core/types';
import { PermissionsService } from '@core/yalo/user';

@Injectable()
export class SuscripcionService {
  private _paymentId = new BehaviorSubject<string>(null);
  private _last4 = new BehaviorSubject<string>(null);
  private _isDefault = new BehaviorSubject<boolean>(false);

  private _subInfo = new BehaviorSubject<Subscriptions>(null);

  private _addons = new BehaviorSubject<SubscriptionsItems[]>(null);
  private _plans = new BehaviorSubject<SubscriptionsItems[]>(null);

  constructor(
    private _http: HttpClient,
    private permissions: PermissionsService
  ) {}

  getPaymentMethods() {
    return this._http.get<Metodos[]>(`${environment.API_URL}/PagosPreferidos`);
  }

  getDefaultCard() {
    return this._http.get<CardsList>(
      `${environment.API_URL}/Stripe/Cards/DefaultInfo`
    );
  }

  updateMetodo(data: Metodos) {
    return this._http.put(
      `${environment.API_URL}/PagosPreferidos/Cambiar`,
      data
    );
  }

  //xxxxx//

  getCards() {
    return this._http.get<CardsList[]>(
      `${environment.API_URL}/Stripe/Cards/List`
    );
  }

  makePayment(method) {
    return this._http.post<string>(
      `${environment.API_URL}/Stripe/ReintentarPagoSubscripcion`,
      method
    );
  }

  getTransferInfo() {
    return this._http
      .get<TransferInfo[]>(
        `${environment.API_URL}/Stripe/Payments/Subs/Transfer/Info`
      )
      .pipe(map((sub) => sub[0]));
  }

  getBancos() {
    return this._http.get<Bancos[]>(
      `${environment.API_URL}/PagosPreferidos/Bancos`
    );
  }
  getInformacionPagoTransferencia() {
    return this._http.get<InformacionPagoTranferencia>(
      `${environment.API_URL}/PagosPreferidos/InformacionPagoTransferencia`
    );
  }

  //xxxxx//

  getCardDetail(id: string) {
    return this._http.get<CardDetail>(
      `${environment.API_URL}/Stripe/Cards/Detail?paymentMethodId=${id}`
    );
  }

  updateCard(detail: CardDetail) {
    return this._http.put<string>(
      `${environment.API_URL}/Stripe/Cards/Update`,
      detail
    );
  }

  setDefaultCard(detail: CardDefault) {
    return this._http.put<string>(
      `${environment.API_URL}/Stripe/Cards/SetDefault`,
      detail
    );
  }

  createCard(create: CardCreate) {
    return this._http.post<string>(
      `${environment.API_URL}/Stripe/Cards/New`,
      create
    );
  }

  deleteCard(id: string) {
    return this._http.delete(
      `${environment.API_URL}/Stripe/Cards/Delete?paymentMethodId=${id}`
    );
  }

  set paymentId(id: string) {
    this._paymentId.next(id);
  }

  get paymentId() {
    return this._paymentId.getValue();
  }

  set last4(id: string) {
    this._last4.next(id);
  }

  get last4() {
    return this._last4.getValue();
  }

  set isDefault(value: boolean) {
    this._isDefault.next(value);
  }

  get isDefault() {
    return this._isDefault.getValue();
  }

  reset() {
    this.paymentId = null;
    this.last4 = null;
    this.isDefault = false;
  }

  //xxxxx// Detalles facturacion

  readonly billingInfo$ = combineLatest([
    this._http.get<BillingInfo>(`${environment.API_URL}/Stripe/Billing/Info`),
    this.permissions.checkAuthorizedPermission(PermisosUsuario.suscripcion)
  ]);

  readonly razonesSociales$ = this._http.get<Sociedades[]>(
    `${environment.API_URL}/TipoSociedades`
  );

  updateBillingInfo(info: BillingInfo) {
    return this._http.post<string>(
      `${environment.API_URL}/Stripe/Billing/Info/Update`,
      info
    );
  }

  //xxxxx//

  getPayments() {
    return this._http.get<PaymentList[]>(
      `${environment.API_URL}/Stripe/Payments/List`
    );
  }

  getNextSub() {
    return this._http
      .get<SubStatus>(`${environment.API_URL}/Stripe/Payments/Subs/Next`)
      .pipe(map((sub) => sub[0]));
  }

  printFactura(factura: PaymentListInvoice) {
    return this._http.post<string>(
      `${environment.API_URL}/FacturaYalo`,
      factura
    );
  }

  //xxxxx//

  getProducts() {
    return this._http
      .get<Products>(`${environment.API_URL}/StripeProductos/SignIn`)
      .pipe(
        map((products) => {
          const addons = products.addons
            .filter((addon) => {
              if (isAddon(addon.Name)) {
                return addon;
              }
            })
            .map((addon) => ({
              ...addon,
              Name: extractAddonName(addon.Name).split(' - ')[0],
            }));
          const plans = products.plans
            .filter((plan) => {
              if (isPlan(plan.Name)) {
                return plan;
              }
            })
            .map((plan) => ({
              ...plan,
              Name: extractPlanName(plan.Name).split('-')[1].trim(),
            }));
          return { addons, plans };
        }),
        tap((data) => {
          this._addons.next(data.addons);
          this._plans.next(data.plans);
        })
      );
  }

  get addons() {
    return this._addons.getValue();
  }

  get plans() {
    return this._plans.getValue();
  }

  getSubList() {
    return this._http
      .get<Subscriptions[]>(`${environment.API_URL}/Stripe/Payments/Subs`)
      .pipe(map((sub) => sub[0]));
  }

  //xxxxx//

  updateSub(data: Subscriptions) {
    return this._http.put(
      `${environment.API_URL}/Stripe/Payments/Subs/Update`,
      data
    );
  }

  cancelSub() {
    return this._http.get<string>(
      `${environment.API_URL}/Stripe/Payments/Subs/Cancel`
    );
  }

  set subInfo(info: Subscriptions) {
    this._subInfo.next(info);
  }

  get subInfo() {
    return this._subInfo.getValue();
  }
}
